/**
 * Mission templates as the Sky Scout LLM agent defines them
 * (ros_ws/src/llm_agent/llm_agent/mission_templates.py). The model picks a
 * template; the safety numbers come from the template, never from the model.
 */

export type FlightPattern =
  | 'spiral'
  | 'circle'
  | 'perimeter'
  | 'zigzag'
  | 'grid'
  | 'waypoints'

export interface MissionTemplate {
  mission_type: string
  target_description: string
  flight_pattern: FlightPattern
  parameters: { altitude: number; speed: number; radius: number }
  safety: {
    geofence: { max_altitude: number; max_distance: number }
    rtl_battery_threshold: number
    weather_check: boolean
    max_wind_speed: number
  }
}

export const MISSION_TEMPLATES = {
  quick_search: {
    mission_type: 'search',
    target_description: 'missing person or object',
    flight_pattern: 'spiral',
    parameters: { altitude: 50, speed: 8, radius: 200 },
    safety: {
      geofence: { max_altitude: 100, max_distance: 300 },
      rtl_battery_threshold: 30,
      weather_check: true,
      max_wind_speed: 12.0,
    },
  },
  building_inspection: {
    mission_type: 'inspect',
    target_description: 'building structure and roof',
    flight_pattern: 'circle',
    parameters: { altitude: 30, speed: 3, radius: 50 },
    safety: {
      geofence: { max_altitude: 60, max_distance: 100 },
      rtl_battery_threshold: 25,
      weather_check: true,
      max_wind_speed: 8.0,
    },
  },
  perimeter_patrol: {
    mission_type: 'patrol',
    target_description: 'security threats or intrusions',
    flight_pattern: 'perimeter',
    parameters: { altitude: 40, speed: 5, radius: 150 },
    safety: {
      geofence: { max_altitude: 80, max_distance: 200 },
      rtl_battery_threshold: 25,
      weather_check: true,
      max_wind_speed: 10.0,
    },
  },
  agricultural_survey: {
    mission_type: 'survey',
    target_description: 'crop health and field conditions',
    flight_pattern: 'zigzag',
    parameters: { altitude: 70, speed: 6, radius: 300 },
    safety: {
      geofence: { max_altitude: 100, max_distance: 500 },
      rtl_battery_threshold: 25,
      weather_check: true,
      max_wind_speed: 10.0,
    },
  },
  parking_count: {
    mission_type: 'count',
    target_description: 'vehicles in parking area',
    flight_pattern: 'grid',
    parameters: { altitude: 40, speed: 4, radius: 100 },
    safety: {
      geofence: { max_altitude: 60, max_distance: 150 },
      rtl_battery_threshold: 25,
      weather_check: true,
      max_wind_speed: 10.0,
    },
  },
  delivery_direct: {
    mission_type: 'delivery',
    target_description: 'delivery location',
    flight_pattern: 'waypoints',
    parameters: { altitude: 60, speed: 10, radius: 10 },
    safety: {
      geofence: { max_altitude: 100, max_distance: 1000 },
      rtl_battery_threshold: 35,
      weather_check: true,
      max_wind_speed: 8.0,
    },
  },
} satisfies Record<string, MissionTemplate>

export type MissionTemplateName = keyof typeof MISSION_TEMPLATES

// A keyword stand-in for the LLM's template choice, so the demo runs offline.
const RULES: [RegExp, MissionTemplateName][] = [
  [/inspect|roof|building|wall|crack/, 'building_inspection'],
  [/patrol|fence|perimeter|secur/, 'perimeter_patrol'],
  [/survey|crop|farm|field health|plant/, 'agricultural_survey'],
  [/count|cars|parking|vehicle/, 'parking_count'],
  [/deliver|drop|bring|carry/, 'delivery_direct'],
  [/search|find|missing|lost|look for|jacket/, 'quick_search'],
]

/** Unmatched commands fall back to a search, as `get_template` does in the real code. */
export function pickTemplate(command: string): {
  name: MissionTemplateName
  matched: boolean
} {
  const text = command.toLowerCase()
  const hit = RULES.find(([re]) => re.test(text))
  return hit
    ? { name: hit[1], matched: true }
    : { name: 'quick_search', matched: false }
}

export type Point = [number, number]

/** Waypoints for each pattern around the origin, `r` in map units. */
export function patternPoints(pattern: FlightPattern, r: number): Point[] {
  const pts: Point[] = []
  if (pattern === 'circle') {
    for (let i = 0; i <= 32; i++) {
      const a = (i / 32) * Math.PI * 2
      pts.push([Math.cos(a) * r, Math.sin(a) * r])
    }
  } else if (pattern === 'spiral') {
    for (let i = 0; i <= 120; i++) {
      const t = i / 120
      const a = t * Math.PI * 2 * 3.5
      pts.push([Math.cos(a) * r * t, Math.sin(a) * r * t])
    }
  } else if (pattern === 'perimeter') {
    const s = r * 0.86
    pts.push([-s, -s], [s, -s], [s, s], [-s, s], [-s, -s])
  } else if (pattern === 'zigzag') {
    const s = r * 0.8
    const n = 7
    for (let i = 0; i < n; i++) {
      const x = -s + i * ((2 * s) / (n - 1))
      pts.push(i % 2 ? [x, s] : [x, -s], i % 2 ? [x, -s] : [x, s])
    }
  } else if (pattern === 'grid') {
    const s = r * 0.78
    const n = 5
    for (let i = 0; i < n; i++) {
      const y = -s + i * ((2 * s) / (n - 1))
      pts.push(i % 2 ? [s, y] : [-s, y], i % 2 ? [-s, y] : [s, y])
    }
    for (let i = 0; i < n; i++) {
      const x = s - i * ((2 * s) / (n - 1))
      pts.push(i % 2 ? [x, -s] : [x, s], i % 2 ? [x, s] : [x, -s])
    }
  } else {
    pts.push([0, 0], [r * 0.55, -r * 0.3], [r * 0.9, -r * 0.42])
  }
  return pts
}

/** Cumulative segment lengths, so the flight can be sampled without measuring the DOM. */
export function polyline(points: Point[]) {
  const cum = [0]
  for (let i = 1; i < points.length; i++) {
    const [ax, ay] = points[i - 1]!
    const [bx, by] = points[i]!
    cum.push(cum[i - 1]! + Math.hypot(bx - ax, by - ay))
  }
  const length = cum[cum.length - 1] ?? 0
  function at(distance: number): Point {
    const d = Math.min(Math.max(distance, 0), length)
    let i = 1
    while (i < cum.length - 1 && cum[i]! < d) i++
    const a = points[i - 1] ?? points[0] ?? [0, 0]
    const b = points[i] ?? a
    const seg = cum[i]! - cum[i - 1]!
    const k = seg > 0 ? (d - cum[i - 1]!) / seg : 0
    return [a[0] + (b[0] - a[0]) * k, a[1] + (b[1] - a[1]) * k]
  }
  return { length, at }
}
